import { getQueryParam } from '@/utils/browser/query'
import { removeLocalStorage, setLocalStorage } from '@/utils/common/storage'

import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useUserStore } from '@/stores/user.stores'
import { useToken } from '@/composables/useToken'
import { STORE_INFO, STORAGE_USER, MEMBER_JOIN } from '@/constants/storageKey.const'

/**
 * 成員邀請路由守衛，用於處理帶有邀請連結的進入流程。
 *
 * 前提：此 middleware 僅掛載於成員邀請相關路由（含 `inviteKeyring` query 參數）。
 *
 * 邏輯：
 * - 若使用者尚未登入或 token 無效，先將 `inviteKeyring` 暫存至 localStorage，
 *   導向登入頁；登入完成後可從 localStorage 讀取並繼續邀請流程。
 * - 若已登入，直接放行至成員加入頁。
 *
 * @remarks `MEMBER_JOIN` localStorage key 由登入後的流程負責讀取並清除。
 */
export const memberJoinMiddleware = ({
  to,
  from,
  next
}: {
  to: RouteLocationNormalized
  from: RouteLocationNormalized
  next: NavigationGuardNext
}) => {
  const userStore = useUserStore()
  const token = userStore.token
  const { checkTokenValidity } = useToken()
  const tokenValid = checkTokenValidity(token)

  const inviteKeyring = getQueryParam('inviteKeyring')

  if (!token || !tokenValid) {
    userStore.initLoginState()
    removeLocalStorage(STORAGE_USER, true)
    removeLocalStorage(STORE_INFO)

    // 暫存邀請憑證，讓使用者登入後可繼續完成加入流程
    setLocalStorage(MEMBER_JOIN, inviteKeyring)
    return next({ name: 'Login' })
  }

  next()
}
